export const CLIENT_OBSERVABILITY_RUNTIMES = [
  "browser",
  "electron_renderer",
  "electron_preload",
  "electron_main",
  "nvzero_helper",
  "react_native",
] as const;

export type ClientObservabilityRuntime = typeof CLIENT_OBSERVABILITY_RUNTIMES[number];

const BROWSER_EVENTS = new Set([
  "browser.api_request.started",
  "browser.api_request.completed",
  "browser.api_request.failed",
  "browser.api_request.cancelled",
  "browser.navigation.started",
  "browser.runtime.error",
  "browser.promise.unhandled",
  "browser.render.error",
  "observability.buffer.dropped",
  "observability.source.heartbeat",
]);

/** React error boundaries that report the render failure they caught (`browser.render.error`). */
export const RENDER_BOUNDARY_SOURCES = [
  "global-error-boundary",
  "route-error-boundary",
  "canvas-load-boundary",
  "report-load-boundary",
  "window-load-boundary",
] as const;

export type RenderBoundarySource = typeof RENDER_BOUNDARY_SOURCES[number];

const EVENT_NAMES: Record<ClientObservabilityRuntime, ReadonlySet<string>> = {
  browser: BROWSER_EVENTS,
  electron_renderer: BROWSER_EVENTS,
  electron_preload: new Set(["observability.source.heartbeat"]),
  electron_main: new Set([
    "electron.main.uncaught_exception",
    "electron.main.unhandled_rejection",
    "electron.renderer.process_gone",
    "electron.child_process.gone",
    "electron.updater.error",
    "electron.updater.event",
    "observability.buffer.dropped",
    "observability.source.heartbeat",
  ]),
  nvzero_helper: new Set([
    "helper.sync.started",
    "helper.sync.completed",
    "observability.buffer.dropped",
    "observability.source.heartbeat",
  ]),
  react_native: new Set([
    "mobile.api_request.started",
    "mobile.api_request.completed",
    "mobile.api_request.failed",
    "observability.buffer.dropped",
    "observability.source.heartbeat",
  ]),
};

const SOURCES: Record<ClientObservabilityRuntime, ReadonlySet<string>> = {
  browser: new Set([
    "browser",
    "browser-startup",
    "browser-buffer",
    "browser-storage",
    "unhandled-rejection",
    "window-error",
    ...RENDER_BOUNDARY_SOURCES,
  ]),
  electron_renderer: new Set([
    "browser-buffer",
    "browser-storage",
    "electron-renderer",
    "electron-renderer-startup",
    "unhandled-rejection",
    "window-error",
    ...RENDER_BOUNDARY_SOURCES,
  ]),
  electron_preload: new Set(["electron-preload-startup"]),
  electron_main: new Set([
    "auto-updater",
    "electron-main",
    "electron-main-buffer",
    "electron-main-startup",
    "electron-main-storage",
    "render-process-gone",
    "child-process-gone",
    "process",
  ]),
  nvzero_helper: new Set(["nvzero-helper", "nvzero-helper-buffer"]),
  react_native: new Set(["mobile-ios", "mobile-android", "native-buffer", "native-storage"]),
};

const SERVICES: Record<ClientObservabilityRuntime, string> = {
  browser: "smartmanager-web",
  electron_renderer: "smartmanager-desktop",
  electron_preload: "smartmanager-desktop",
  electron_main: "smartmanager-desktop",
  nvzero_helper: "smartmanager-helper",
  react_native: "smartmanager-mobile",
};

export function isClientObservabilityRuntime(value: unknown): value is ClientObservabilityRuntime {
  return typeof value === "string"
    && (CLIENT_OBSERVABILITY_RUNTIMES as readonly string[]).includes(value);
}

export function clientObservabilityService(runtime: ClientObservabilityRuntime): string {
  return SERVICES[runtime];
}

/** Prevents an authenticated client from spoofing server/provider completeness. */
export function isAllowedClientOperationalEvent(input: {
  runtime: ClientObservabilityRuntime;
  eventName: string;
  source?: string;
}): boolean {
  return EVENT_NAMES[input.runtime].has(input.eventName)
    && (!input.source || SOURCES[input.runtime].has(input.source));
}
